"use client";

import { c as _c } from "react/compiler-runtime";
import { LINKS } from "@consts";
import { Eye, EyeOff, Key01 } from "@untitledui/icons";
import { cx } from "@utils/cx";
import { arePasskeysAvailable } from "@utils/environment";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Form } from "@/components/base/form/form";
import { Input } from "@/components/base/input/input";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { authClient, signIn } from "@/lib/auth-client";
import { sanitizePostLoginPath } from "@/lib/post-login-redirect";
import { capturePosthogEvent, identifyPosthog } from "@/lib/posthog-browser";
import { loginSchema } from "@/lib/validations/user";
export default function LoginForm({
  showSignUp = false,
  isSuccess,
  setIsSuccess,
  postLoginRedirect = "/pools"
}: {
  showSignUp?: boolean;
  isSuccess: boolean;
  setIsSuccess: (value: boolean) => void;
  /** After email/passkey login success — must be same-origin path (validated by caller). */
  postLoginRedirect?: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /** Synced in onChange so blur/submit never read stale controlled values (Enter / autofill races). */
  const emailRef = useRef(email);
  const passwordRef = useRef(password);
  const skipPasswordBlurValidationRef = useRef(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const passkeysAvailable = arePasskeysAvailable();

  // Validate form using Zod schema (reads refs — same values submit uses)
  const validateForm = (markAllTouched = false, newlyTouchedField?: string) => {
    const result = loginSchema.safeParse({
      email: emailRef.current.trim(),
      password: passwordRef.current.trim(),
      rememberMe
    });
    if (!result.success) {
      const errors: Record<string, string> = {};
      // Determine which fields should show errors
      const fieldsToShowErrors = markAllTouched ? new Set(result.error.issues.map(issue => issue.path[0]?.toString()).filter(Boolean)) : new Set([...Object.keys(touchedFields).filter(key => touchedFields[key]), ...(newlyTouchedField ? [newlyTouchedField] : [])]);
      result.error.issues.forEach(issue_0 => {
        if (issue_0.path[0]) {
          const fieldName = issue_0.path[0].toString();
          // Only show error if field is touched (or if marking all as touched)
          if (fieldsToShowErrors.has(fieldName)) {
            errors[fieldName] = issue_0.message;
          }
        }
      });
      setFieldErrors(errors);
      if (markAllTouched) {
        // Mark all fields with errors as touched
        setTouchedFields(prev => {
          const next = {
            ...prev
          };
          result.error.issues.forEach(issue_1 => {
            if (issue_1.path[0]) {
              next[issue_1.path[0].toString()] = true;
            }
          });
          return next;
        });
      }
      return false;
    } else {
      setFieldErrors({});
      return true;
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    const result_0 = loginSchema.safeParse({
      email: emailRef.current.trim(),
      password: passwordRef.current.trim(),
      rememberMe
    });
    return result_0.success;
  };

  // Handle redirect after success state is shown for at least 2 seconds
  useEffect(() => {
    if (isSuccess) {
      const redirectTimer = setTimeout(() => {
        window.location.href = postLoginRedirect;
      }, 2000);
      return () => clearTimeout(redirectTimer);
    }
  }, [isSuccess, postLoginRedirect]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    skipPasswordBlurValidationRef.current = false;
    setError(null);

    // Validate form before submitting (mark all fields as touched)
    if (!validateForm(true)) {
      return;
    }
    setIsLoading(true);
    try {
      const emailTrimmed = emailRef.current.trim();
      const passwordTrimmed = passwordRef.current.trim();
      const result_1 = await signIn.email({
        email: emailTrimmed,
        password: passwordTrimmed,
        rememberMe
      });
      if (result_1.error) {
        setError(result_1.error.message || "Invalid email or password");
        setIsLoading(false);
        // Capture login failed event with PostHog
        void capturePosthogEvent("login_failed", {
          error_message: result_1.error.message || "Invalid credentials",
          login_method: "email_password"
        });
        // Log failed login attempt
        try {
          await fetch("/api/auth/log-failed-login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              email: emailTrimmed,
              reason: result_1.error.message || "Invalid credentials"
            })
          });
        } catch (logError) {
          // Silently fail - don't break login flow
          console.error("Failed to log failed login attempt:", logError);
        }
      } else {
        let posthogId: string | null = null;
        let isTestUser = false;
        try {
          const pr = await fetch("/api/user/profile");
          if (pr.ok) {
            const body = (await pr.json()) as {
              user?: {
                id?: string;
                isTest?: boolean;
              };
            };
            posthogId = body.user?.id ?? null;
            isTestUser = body.user?.isTest === true;
          }
        } catch {
          /* ignore */
        }
        if (posthogId) {
          await identifyPosthog(posthogId, {
            is_test_user: isTestUser
          });
        }
        await capturePosthogEvent("user_logged_in", {
          login_method: "email_password",
          remember_me: rememberMe
        });
        // Show success state - redirect will happen after 2 seconds via useEffect
        setIsLoading(false);
        setIsSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
      setIsLoading(false);
    }
  };

  // Helper function to detect user cancellation errors
  const isUserCancellationError = (errorMessage: string): boolean => {
    const lowerMessage = errorMessage.toLowerCase();
    return lowerMessage.includes("cancel") || lowerMessage.includes("abort") || lowerMessage.includes("not allowed") || lowerMessage.includes("timed out") || lowerMessage.includes("the operation either timed out");
  };
  const handlePasskeyLogin = async () => {
    setError(null);
    setIsPasskeyLoading(true);
    try {
      const result_2 = await authClient.signIn.passkey({
        autoFill: false
      });
      if (result_2.error) {
        const errorMessage_1 = result_2.error.message || "";
        // Check if user cancelled the authentication
        if (isUserCancellationError(errorMessage_1)) {
          // Don't show error for user cancellation - it's expected behavior
          setError(null);
        } else {
          setError(errorMessage_1 || "Failed to sign in with passkey");
          void capturePosthogEvent("login_failed", {
            error_message: errorMessage_1 || "Failed to sign in with passkey",
            login_method: "passkey"
          });
        }
      } else {
        let posthogId_0: string | null = result_2.data?.user?.id ?? null;
        let isTestUser_0 = false;
        try {
          const pr_0 = await fetch("/api/user/profile");
          if (pr_0.ok) {
            const body_0 = (await pr_0.json()) as {
              user?: {
                id?: string;
                isTest?: boolean;
              };
            };
            posthogId_0 = body_0.user?.id ?? posthogId_0;
            isTestUser_0 = body_0.user?.isTest === true;
          }
        } catch {
          /* ignore */
        }
        if (posthogId_0) {
          await identifyPosthog(posthogId_0, {
            is_test_user: isTestUser_0
          });
        }
        await capturePosthogEvent("user_logged_in_passkey", {
          login_method: "passkey"
        });

        // Show success state - redirect will happen after 2 seconds via useEffect
        setIsPasskeyLoading(false);
        setIsSuccess(true);
      }
    } catch (err_0) {
      // Check if this is a user cancellation error
      const isCancellation = err_0 instanceof DOMException ? err_0.name === "NotAllowedError" || err_0.name === "AbortError" : err_0 instanceof Error && isUserCancellationError(err_0.message);
      if (isCancellation) {
        // Don't show error for user cancellation - silently handle it
        setError(null);
      } else {
        const errorMessage_0 = err_0 instanceof Error ? err_0.message : "An unexpected error occurred";
        setError(errorMessage_0);
        // Capture passkey login error with PostHog
        void capturePosthogEvent("login_failed", {
          error_message: errorMessage_0,
          login_method: "passkey"
        });
      }
    } finally {
      // Always reset loading state, even on cancellation
      setIsPasskeyLoading(false);
    }
  };

  // Show success message instead of form when login is successful
  if (isSuccess) {
    return <div className="flex w-full flex-col items-center justify-center gap-6 py-8">
				<LoadingIndicator type="line-spinner" size="md" label="Successfully logged in" />
			</div>;
  }
  return <Form onSubmit={handleSubmit} className="flex w-full flex-col items-center justify-center gap-6">
			{error && <div className="w-full rounded-md bg-error-primary/10 p-3 text-sm text-error-primary" role="alert" aria-live="polite">
					{error}
				</div>}

			{/* Passkey login button */}
			<Tooltip title={passkeysAvailable ? undefined : "Passkeys disabled in preview environments"} isDisabled={passkeysAvailable}>
				<div className={cx("w-full transition-opacity duration-200", !passkeysAvailable && "opacity-50")}>
					<Button type="button" color="secondary" size="lg" className={cx("w-full", !passkeysAvailable && "pointer-events-none")} iconLeading={Key01} onClick={handlePasskeyLogin} isDisabled={!passkeysAvailable || isLoading || isPasskeyLoading} isLoading={isPasskeyLoading} showTextWhileLoading>
						{isPasskeyLoading ? "Signing in with passkey..." : "Sign In With Passkey"}
					</Button>
				</div>
			</Tooltip>

			<Tooltip title={passkeysAvailable ? undefined : "Passkeys disabled in preview environments"} isDisabled={passkeysAvailable}>
				<div className={cx("flex w-full items-center gap-2 transition-opacity duration-200", !passkeysAvailable && "opacity-50")}>
					<div className="h-px flex-1 bg-secondary_alt"></div>
					<span className="text-sm text-tertiary">or</span>
					<div className="h-px flex-1 bg-secondary_alt"></div>
				</div>
			</Tooltip>

			{/* Email/password form - reduced opacity when passkey is loading */}
			<div className={cx("w-full space-y-6 transition-opacity duration-200", isPasskeyLoading && "pointer-events-none opacity-50")}>
				<Input isRequired label="Email" placeholder="Enter Your Email" size="md" value={email} onChange={value => {
        emailRef.current = value;
        setEmail(value);
        // Clear field error when user starts typing
        if (fieldErrors.email) {
          setFieldErrors(prev_0 => {
            const next_0 = {
              ...prev_0
            };
            delete next_0.email;
            return next_0;
          });
        }
      }} onBlur={() => {
        setTouchedFields(prev_1 => ({
          ...prev_1,
          email: true
        }));
        queueMicrotask(() => validateForm(false, "email"));
      }} type="email" isDisabled={isLoading || isPasskeyLoading} isInvalid={!!fieldErrors.email} hint={fieldErrors.email} autoComplete="username" />
				<div className="relative w-full">
					<Input isRequired label="Password" placeholder="••••••••" size="md" value={password} onChange={value_0 => {
          passwordRef.current = value_0;
          setPassword(value_0);
          // Clear field error when user starts typing
          if (fieldErrors.password) {
            setFieldErrors(prev_2 => {
              const next_1 = {
                ...prev_2
              };
              delete next_1.password;
              return next_1;
            });
          }
        }} onKeyDown={e_0 => {
          if (e_0.key === "Enter") {
            skipPasswordBlurValidationRef.current = true;
          }
        }} onBlur={() => {
          if (skipPasswordBlurValidationRef.current) {
            skipPasswordBlurValidationRef.current = false;
            return;
          }
          setTouchedFields(prev_3 => ({
            ...prev_3,
            password: true
          }));
          queueMicrotask(() => validateForm(false, "password"));
        }} type={showPassword ? "text" : "password"} inputClassName={fieldErrors.password ? "pr-19" : "pr-11"} isDisabled={isLoading || isPasskeyLoading} isInvalid={!!fieldErrors.password} hint={fieldErrors.password} autoComplete="current-password" />
					{/* Label (20px) + gap (6px) offsets the toggle to the md input box (44px). */}
					<button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(prev_4 => !prev_4)} disabled={isLoading || isPasskeyLoading} className={cx("absolute top-[26px] flex size-11 items-center justify-center rounded-lg text-fg-quaternary outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled lg:hover:text-fg-quaternary_hover", fieldErrors.password ? "right-8" : "right-0")}>
						{showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
					</button>
				</div>
				<div className="flex w-full items-center justify-between gap-2">
					<Checkbox label="Remember For 30 Days" size="sm" isSelected={rememberMe} onChange={setRememberMe} isDisabled={isLoading || isPasskeyLoading} />
					<Link {...LINKS.ForgotPassword} className="text-sm font-semibold text-brand-secondary lg:hover:text-brand-secondary_hover">
						Forgot password?
					</Link>
				</div>
				<Button type="submit" color="primary" size="lg" className="w-full" isDisabled={!isFormValid() || isLoading || isPasskeyLoading} isLoading={isLoading} showTextWhileLoading>
					{isLoading ? "Signing in..." : "Sign In"}
				</Button>
			</div>
			{showSignUp && <div className="flex items-center justify-center text-center">
					<p className="text-sm font-light text-tertiary">
						Don't have an account?{" "}
						<Link {...LINKS.Plans} className="font-semibold text-brand-secondary lg:hover:text-brand-secondary_hover">
							Sign up
						</Link>
					</p>
				</div>}
		</Form>;
}
export function LoginFormWrapper(t0) {
  const $ = _c(34);
  const {
    classNames,
    title,
    description,
    showSignUp
  } = t0;
  const [isSuccess, setIsSuccess] = useState(false);
  const [sessionGate, setSessionGate] = useState("pending");
  const searchParams = useSearchParams();
  const router = useRouter();
  let t1;
  if ($[0] !== searchParams) {
    t1 = sanitizePostLoginPath(searchParams.get("callbackUrl"));
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const postLoginRedirect = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      let cancelled = false;
      authClient.getSession().then(t4 => {
        const {
          data
        } = t4;
        if (cancelled) {
          return;
        }
        setSessionGate(data?.user ? "authenticated" : "anonymous");
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== postLoginRedirect || $[5] !== router || $[6] !== sessionGate) {
    t4 = () => {
      if (sessionGate !== "authenticated") {
        return;
      }
      router.replace(postLoginRedirect);
    };
    t5 = [sessionGate, postLoginRedirect, router];
    $[4] = postLoginRedirect;
    $[5] = router;
    $[6] = sessionGate;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (sessionGate === "pending" || sessionGate === "authenticated") {
    const t6 = classNames?.root;
    let t7;
    if ($[9] !== t6) {
      t7 = cx("flex w-full max-w-96 flex-col items-center justify-center gap-4 py-8", t6);
      $[9] = t6;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <LoadingIndicator size="md" />;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    const t9 = sessionGate === "authenticated" ? "Taking you to Unilytics\u2026" : "Loading\u2026";
    let t10;
    if ($[12] !== t9) {
      t10 = <p className="text-sm text-tertiary">{t9}</p>;
      $[12] = t9;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10 || $[15] !== t7) {
      t11 = <div className={t7}>{t8}{t10}</div>;
      $[14] = t10;
      $[15] = t7;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    return t11;
  }
  const t6 = classNames?.root;
  let t7;
  if ($[17] !== t6) {
    t7 = cx("w-full max-w-96", t6);
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isSuccess || $[20] !== title) {
    t8 = !isSuccess && title && <h1 className="mb-3 text-display-md font-semibold text-primary">{title}</h1>;
    $[19] = isSuccess;
    $[20] = title;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== description || $[23] !== isSuccess) {
    t9 = !isSuccess && description && <p className="mb-6 text-md text-tertiary">{description}</p>;
    $[22] = description;
    $[23] = isSuccess;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isSuccess || $[26] !== postLoginRedirect || $[27] !== showSignUp) {
    t10 = <LoginForm showSignUp={showSignUp} isSuccess={isSuccess} setIsSuccess={setIsSuccess} postLoginRedirect={postLoginRedirect} />;
    $[25] = isSuccess;
    $[26] = postLoginRedirect;
    $[27] = showSignUp;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t11 = <div className={t7}>{t8}{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}

// Attach Wrapper to default export for backward compatibility
const LoginFormWithWrapper = LoginForm as typeof LoginForm & {
  Wrapper: typeof LoginFormWrapper;
};
LoginFormWithWrapper.Wrapper = LoginFormWrapper;